import { compose, deburr, filter, get, isEmpty, size as lodashSize, toLower, trim } from 'lodash/fp';
import { Children, cloneElement, Component, isValidElement, ReactNode } from 'react';
import { isIos, isMobileDevice } from '@optimus-web/common';
import {
    ClickAwayProps,
    MaxSizeChangeProps,
    ScrollAwayProps,
    withClickAway,
    withScrollAway,
} from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { AutosizeInput as AutosizeInputComponent } from '../AutosizeInput';
import { Chip } from '../Chip';
import { CircleLoader } from '../CircleLoader';
import { Affix } from '../common/Affix';
import { RestTagCounterContainer, RightDetailContainer } from '../commonStyle';
import { Divider } from '../Divider';
import { ExpandIcon } from '../ExpandIcon';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { Input } from '../Input';
import { ListItemProps } from '../ListItem';
import { IOpenableOverlay, OpenableOverlay } from '../OpenableOverlay';
import { ISelectList, SelectList } from '../SelectList';
import { Stack } from '../Stack';
import { InputValue, Labeled, RenderDropdownIconProps, Selected, SizeType, Valued } from '../types';
import { isDescendant } from '../utils/events';
import { mod } from '../utils/func';
import { SelectDefaultedProps } from './SelectDefaultedProps';
import {
    ComboInputElement,
    ComboInputElementProps,
    LeftVariantContainer,
    LoaderContainer,
    RightVariantContainer,
    SearchContainerElement,
    SingleValueElement,
    ValueContainerElement,
} from './style';

const normalize = compose(trim, toLower, deburr);

interface OnClickEvents extends Valued {
    onClick?: (e: React.MouseEvent, v: InputValue) => void;
    onAfterClick?: (e: React.MouseEvent, v: InputValue) => void;
}

type SelectListItemProps = Omit<ListItemProps, keyof OnClickEvents> & OnClickEvents;

export interface SelectProps extends ComboInputElementProps, ScrollAwayProps, ClickAwayProps {
    name?: string;
    selected: Selected | undefined;
    multiselect?: boolean;
    virtualized?: boolean;
    defaultItemHeight?: number;
    overlayMaxHeight?: number;
    sizeToAnchor?: boolean;
    inputType?: React.HTMLInputTypeAttribute;
    /**
     * Regexp pattern for checking input's value while typing
     */
    inputValuePattern: RegExp;

    valueMapper: (c: React.ReactElement) => InputValue;
    searchValueMapper: (c: React.ReactElement<Labeled>) => string;
    displayMapper: (c: React.ReactElement<Labeled>) => React.ReactNode;
    emptyMessage: React.ReactNode;
    renderDropdownIcon?: (o: RenderDropdownIconProps) => React.ReactNode;

    onFocus?: () => void;
    onBlur?: () => void;
    onChange?: (e: React.ChangeEvent, s: Selected) => void;
    onClear?: () => void;
    onSearchChange?: (searchValue: string) => void;

    shadowField?: boolean;
    value?: InputValue;
    portalContainerId?: string;
    placeholder?: React.ReactNode;
    searchPlaceholder?: React.ReactNode;
    size?: SizeType;
    required?: boolean;
    autocomplete?: boolean;
    compact?: boolean;

    iconName?: IconName;
    leadingImage?: string;
    trailingImage?: string;
    suffix?: ReactNode;
    loader?: boolean;
}

interface SelectState {
    opened: boolean;
    focused: boolean;
    selectedIndex: number;
    searchValue: string;
    keyboardHandled: boolean;
    indexChangeReason?: 'user' | 'internal';
    restTagCount: number;
}

class DefaultSelect extends Component<SelectProps, SelectState> {
    static defaultProps = {
        sizeToAnchor: true,
        overlayMaxHeight: 500,
        valueMapper: (c: React.ReactElement<Valued>) => c.props.value,
        searchValueMapper: (c: React.ReactElement<Labeled>) => {
            const normalizedPrimaryText = typeof c.props.primaryText === 'string' ? normalize(c.props.primaryText) : '';
            const normalizedSecondaryText =
                typeof c.props.secondaryText === 'string' ? normalize(c.props.secondaryText) : '';
            return `${normalizedPrimaryText} ${normalizedSecondaryText}`.trim(); // trim it to not return one space but empty string instead
        },
        displayMapper: (c: React.ReactElement<Labeled>) => c.props.primaryText,
        renderDropdownIcon: ({ toggle, open, disabled }: RenderDropdownIconProps) => (
            <ExpandIcon disabled={disabled} onClick={toggle} expanded={open} />
        ),
    };

    readonly state: SelectState = {
        opened: false,
        focused: false,
        selectedIndex: 0,
        searchValue: '',
        keyboardHandled: false,
        restTagCount: 0,
    };

    private comboInput?: HTMLElement | null;
    private input?: HTMLInputElement | null;
    private overlay?: HTMLElement | null;
    private overlayComponent: IOpenableOverlay | null = null;
    private listComponent: ISelectList | null = null;
    private valueContainer: HTMLDivElement | null = null;
    private tags: Record<InputValue, HTMLDivElement | null> = {};

    private blurHandler?: number;

    private items: React.ReactElement[] = [];

    componentDidMount = () => {
        const { multiselect, compact } = this.props;
        if (multiselect && compact) {
            this.handleRestTagCounter();
        }
    };

    componentDidUpdate(prevProps: SelectProps, prevState: SelectState) {
        if (
            (prevState.opened && !this.state.opened) ||
            (prevState.searchValue.length > 0 && prevProps.selected !== this.props.selected)
        ) {
            this.selectIndexZero();

            if (!this.props.autocomplete) {
                this.clearSearchValue();
            }
        }

        // Change of value can affect number of displayed children and thus height of the overlay.
        // For performance gains, it's better to trigger placement check manually than do it automatically on update inside OpenableOverlay
        if (this.state.opened && this.overlayComponent) {
            this.overlayComponent.updatePlacement();
        }
        this.handleRestTagCounter();
    }

    componentWillUnmount() {
        if (this.blurHandler) {
            window.clearTimeout(this.blurHandler);
            this.blurHandler = undefined;
        }
    }

    selectIndexZero = () => {
        this.setState({ selectedIndex: 0, indexChangeReason: 'internal' });
    };

    clearSearchValue = () => {
        this.setState({ searchValue: '' });
    };

    focusInput = () => {
        this.input?.focus({
            preventScroll: true,
        });
    };

    open = () => {
        if (!this.state.opened && !this.props.disabled) {
            this.setState({ opened: true });

            if (this.input && this.props.multiselect && this.props.compact) {
                this.focusInput();
            }

            const roots = [this.comboInput, () => this.overlay];
            this.props.registerClickAway({ handler: isIos() ? this.handleIOSClickAway : this.close, roots });

            if (!isMobileDevice()) {
                this.props.registerScrollAway({ handler: this.close, roots });
            }
        }
    };

    close = () => {
        if (this.state.opened) {
            this.setState({ opened: false });
            this.props.deregisterScrollAway();
            this.props.deregisterClickAway();
        }

        if (this.state.focused) {
            this.setState({ focused: false });
        }

        if (this.state.searchValue && !this.props.autocomplete) {
            this.clearSearchValue();
        }

        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    toggle = () => {
        if (this.state.opened) {
            this.close();
        } else {
            this.open();
        }
    };

    handleIOSClickAway = () => {
        this.setState({ focused: false });
        if (this.props.onBlur) {
            this.props.onBlur();
        }
        this.close();
    };

    handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        this.setState({ searchValue: value, selectedIndex: 0, indexChangeReason: 'internal' });
        this.props.onSearchChange?.(value);

        if (this.props.onChange && this.props.autocomplete) {
            this.props.onChange(e, value);
        }

        if (!this.state.opened && value.length > 0) {
            this.open();
        }
    };

    handleFocus = () => {
        if (!this.state.focused) {
            this.setState({ focused: true });

            if (this.props.onFocus) {
                this.props.onFocus();
            }
        }
    };

    handleBlur = () => {
        if (this.state.focused) {
            this.blurHandler = window.setTimeout(this.bindedDeferredBlurHandler);
        }
    };

    handleDeferredBlur = () => {
        this.blurHandler = undefined;

        // This captures closing virtual keyboard on iOS, but not i.e. focusing next field.
        // Click out of the component is still captured by ClickAway
        const shouldNotCloseOnIOS = document.activeElement && document.activeElement.tagName === 'BODY';
        if (isIos() && shouldNotCloseOnIOS) {
            return;
        }

        const roots = [this.comboInput, this.overlay];
        const shouldClose = !roots.some(
            (r) => typeof r !== 'undefined' && r !== null && isDescendant(document.activeElement as HTMLElement, r)
        );

        if (shouldClose) {
            this.close();
        }
    };

    bindedDeferredBlurHandler = () => this.handleDeferredBlur();

    handleComboClick = () => {
        if (!this.state.opened) {
            this.open();
        }

        if (this.input) {
            this.focusInput();
        }
    };

    handleItemClick = (e: React.BaseSyntheticEvent, value: InputValue) => {
        if (this.props.onChange) {
            if (this.props.multiselect) {
                const prevSelected = this.props.selected as InputValue[];
                let selected: Selected;
                if (prevSelected.includes(value)) {
                    selected = prevSelected.filter((v) => v !== value);
                } else {
                    selected = [...prevSelected, value];
                }

                this.props.onChange(e as React.ChangeEvent, selected);
            } else {
                this.props.onChange(e as React.ChangeEvent, value);
            }
        }

        if (this.props.autocomplete) {
            this.setState({ searchValue: value as string });
            this.props.onSearchChange?.(value as string);
        }

        if (!this.props.multiselect) {
            this.close();
        }

        this.handleAfterItemClick();
    };

    handleAfterItemClick = () => {
        if (this.props.multiselect) {
            if (this.input) {
                this.focusInput();
            }
        } else if (this.comboInput) {
            this.comboInput.focus();
        }
    };

    handleHover = (index: number) => () => {
        this.setState({
            keyboardHandled: false,
            selectedIndex: index,
            indexChangeReason: 'user',
        });
    };

    handleRemoveItem = (e: React.BaseSyntheticEvent, index: number | string | boolean) => {
        if (this.props.onChange && this.props.multiselect) {
            const selected = [...(this.props.selected as InputValue[])];
            selected.splice(index as number, 1);
            this.props.onChange(e as React.ChangeEvent, selected);
        }
    };

    handleComboKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (this.input) {
            this.focusInput();
        }

        this.handleInputKeyDown(e);
    };

    handleInputKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (!this.items) {
            return;
        }

        let handled;

        if (e.key === 'ArrowDown') {
            if (this.state.opened && this.items.length > 0) {
                this.setState((state) => ({
                    selectedIndex: (state.selectedIndex + 1) % this.items.length,
                    indexChangeReason: 'user',
                }));
                handled = true;
            }

            if (!this.state.opened) {
                this.open();
                handled = true;
            }
        } else if (e.key === 'ArrowUp') {
            if (this.state.opened && this.items.length > 0) {
                this.setState((state) => ({
                    selectedIndex: mod(state.selectedIndex - 1, this.items.length),
                    indexChangeReason: 'user',
                }));
                handled = true;
            }

            if (!this.state.opened) {
                this.open();
                handled = true;
            }
        } else if (e.key === 'Enter') {
            if (this.state.opened && this.items.length > 0 && this.shouldShowDropdown()) {
                const item = this.items[this.state.selectedIndex];

                /*
                 * HACK: This is quite ugly manual event triggering and is needed mainly because of custom onClick support, i.e. SelectAll in Commander.
                 * Question is how to solve it better?
                 */
                const menuItem = item as React.ReactElement<{
                    onClick: (e: React.SyntheticEvent, value: InputValue) => void;
                    value: InputValue;
                    disabled?: boolean;
                }>;

                if (!menuItem.props.disabled) {
                    menuItem.props.onClick(e, menuItem.props.value);
                }

                if (this.props.autocomplete) {
                    this.setState({ searchValue: menuItem.props.value as string });
                    this.props.onSearchChange?.(menuItem.props.value as string);
                }

                handled = true;
            }
        } else if (e.key === 'Escape') {
            if (this.state.opened) {
                this.close();
                handled = true;
            }
        } else if (e.key === 'Backspace') {
            if (this.props.multiselect) {
                const selected = this.props.selected as InputValue[];
                if (this.state.searchValue === '' && selected.length > 0) {
                    this.handleRemoveItem(e, selected.length - 1);
                    handled = true;
                }
            }
        } else if (
            this.props.inputValuePattern &&
            !this.props.inputValuePattern.test((e.target as HTMLInputElement).value + e.key)
        ) {
            // for preventing default behavior if current value does not match regex if given
            handled = true;
        }

        if (handled) {
            e.preventDefault();
            e.stopPropagation();
            this.setState({
                keyboardHandled: true,
            });
        }
    };

    handleMaxSizeChange = ({ maxHeight }: MaxSizeChangeProps) => {
        if (this.listComponent) {
            if (maxHeight) {
                this.listComponent.updateMaxHeight(maxHeight);
            }
        }
    };

    handleRestTagCounter = () => {
        if (this.valueContainer) {
            this.valueContainer.scrollTop = 0;
            const { bottom: valueContainerBottom } = this.valueContainer.getBoundingClientRect();
            const restTagCount = compose(
                lodashSize,
                filter<HTMLDivElement | null>(
                    (tag) => !!tag && tag.getBoundingClientRect().bottom > valueContainerBottom
                )
            )(this.tags);

            if (restTagCount !== this.state.restTagCount) {
                this.setState({ restTagCount });
            }
        }
    };

    handleOverlayOnMount = () => {
        const { multiselect, compact } = this.props;
        if (this.input && multiselect === true && compact === true) {
            this.focusInput();
        }
    };

    handleComboInputRef = (c: HTMLElement | null) => (this.comboInput = c);
    handleInputRef = (c: HTMLInputElement | null) => (this.input = c);
    handleOverlayRef = (c: HTMLElement | null) => (this.overlay = c);
    handleOverlayComponentRef = (c: IOpenableOverlay | null) => (this.overlayComponent = c);
    handleListRef = (c: ISelectList | null) => (this.listComponent = c);
    handleValueContainerRef = (c: HTMLDivElement | null) => (this.valueContainer = c);
    handleTagRef = (key: InputValue) => (c: HTMLDivElement | null) => (this.tags[key] = c);

    getAnchorElement = () => this.comboInput;

    shouldShowDropdown = () => !this.props.autocomplete || (this.items.length > 0 && this.state.searchValue.length > 0);

    isSelected = (c: React.ReactElement<Valued>) => {
        const { multiselect, selected, valueMapper } = this.props;
        const value = valueMapper(c);
        return multiselect ? (selected as InputValue[]).includes(value) : selected === value;
    };

    // TODO LET'S DO OPTIMIZATION BY HAVING CHILDRENS MAPPED AS ORIGINAL SELECT HAD THEM
    getDisplayValues() {
        const { selected, children, valueMapper, displayMapper, multiselect } = this.props;
        const displayedValues: Array<[InputValue, React.ReactNode]> = [];

        Children.forEach(children, (c) => {
            if (!isValidElement<Valued & Labeled>(c)) {
                return;
            }

            const value = valueMapper(c);
            if (multiselect) {
                // for multiselect we want to display in order in which they are selected
                const index = (selected as InputValue[]).indexOf(value);
                if (index !== -1) {
                    displayedValues[index] = [value, displayMapper(c)];
                }
            } else if (selected === value) {
                displayedValues.push([value, displayMapper(c)]);
            }
        });

        return displayedValues;
    }

    renderLeftVariant() {
        const { iconName, leadingImage } = this.props;

        if (iconName || leadingImage) {
            return (
                <LeftVariantContainer>
                    {iconName && <Icon value={iconName} />}
                    {leadingImage && <Image src={leadingImage} />}
                </LeftVariantContainer>
            );
        }

        return null;
    }

    renderRightVariant() {
        const { suffix, trailingImage, loader } = this.props;

        if (suffix || trailingImage || loader) {
            return (
                <RightVariantContainer>
                    <Stack spacing="100" itemAlignment="center">
                        {suffix && <Affix>{suffix}</Affix>}
                        {trailingImage && <Image src={trailingImage} />}
                    </Stack>
                    {loader && (
                        <LoaderContainer>
                            <CircleLoader size="small" />
                        </LoaderContainer>
                    )}
                </RightVariantContainer>
            );
        }

        return null;
    }

    render() {
        const {
            name,
            multiselect,
            selected,
            children,
            searchValueMapper,
            shadowField,
            overlayMaxHeight,
            renderDropdownIcon,
            portalContainerId,
            placeholder,
            searchPlaceholder,
            virtualized,
            defaultItemHeight,
            disabled,
            required,
            size,
            emptyMessage,
            sizeToAnchor,
            value,
            compact,
            inputType,

            // internal props
            onChange,
            onBlur,
            onClear,
            onFocus,
            onSearchChange,
            registerClickAway,
            registerScrollAway,
            deregisterClickAway,
            deregisterScrollAway,
            displayMapper,
            valueMapper,

            ...other
        } = this.props;

        const { searchValue, selectedIndex, indexChangeReason, opened, restTagCount } = this.state;

        const normalizedSearchValue = normalize(searchValue);
        const indexChangeRequestedByUser = indexChangeReason === 'user';

        const filteredItems = Children.toArray(children).filter((c): c is React.ReactElement<SelectListItemProps> => {
            if (!isValidElement<Labeled>(c)) {
                return false;
            }

            if (!normalizedSearchValue.length || onSearchChange) {
                return true;
            }

            // use mapped search value or stringified value for comparation
            const childSearchValue: Selected | null = searchValueMapper(c) || valueMapper(c)?.toString();

            if (childSearchValue) {
                // return if child search value is a part of searching query
                return childSearchValue.includes(normalizedSearchValue);
            }

            return false;
        });

        this.items = filteredItems.map((c, index) => {
            const focused = index === selectedIndex;
            return cloneElement(c, {
                multiselect,
                focused,
                shouldScrollIntoView: indexChangeRequestedByUser && this.state.keyboardHandled,
                keyboardHandled: this.state.keyboardHandled,
                selected: c.props.selected || this.isSelected(c as React.ReactElement<Valued>),
                onClick: c.props.onClick || this.handleItemClick,
                onAfterClick: this.handleAfterItemClick,
                onMouseEnter: this.handleHover(index),
                tabIndex: -1,
                role: 'option',
                id: `${name}-${c.props.value}`,
                'aria-selected': focused,
            });
        });

        const comboInputProps = {
            ...other,
            value,
            disabled,
            ref: this.handleComboInputRef,
            onClick: !disabled ? this.handleComboClick : undefined,
            // Prevents focus by tab, but catch focus by clicking inside - this allows the defferedBlur to work correctly
            tabIndex: -1,
            onKeyDown: this.handleComboKeyDown,
        };

        const displayValues = this.getDisplayValues();

        const activeValue = this.items.length > 0 ? this.items[selectedIndex]?.props.value : null;
        const displayedInputValue = get(['0', '1'], displayValues);

        const inputProps = {
            disabled,
            required,
            placeholder: typeof selected !== 'number' && isEmpty(selected) ? placeholder : '',
            value: searchValue,
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            ref: this.handleInputRef,
            onChange: this.handleSearchValueChange,
            onKeyDown: this.handleInputKeyDown,
            inputSize: size,
            id: name,
            role: 'combobox',
            type: inputType,
            'aria-activedescendant': this.state.opened && activeValue ? `${name}-${activeValue}` : undefined,
            'aria-autocomplete': 'list',
            'aria-expanded': this.state.opened,
            'aria-haspopup': 'listbox',
            'data-test-display-value': displayedInputValue,
        };

        const listProps = {
            virtualized,
            defaultItemHeight: defaultItemHeight as number,
            selectedIndex,
            maxHeight: overlayMaxHeight as number,
            emptyMessage,
            ref: this.handleListRef,
            keyboardHandled: this.state.keyboardHandled,
        };

        const autocompleteProps = {
            ...inputProps,
            value: searchValue || value,
            ref: this.handleComboInputRef,
            onClick: !disabled ? this.handleComboClick : undefined,
            name,
        };

        const renderDropdownIconProps = {
            toggle: this.toggle,
            open: this.state.opened,
            disabled,
        };

        const leftVariant = this.renderLeftVariant();
        const rightVariant = this.renderRightVariant();

        const valueContainerProps = {
            rightDetail: renderDropdownIcon,
            size,
            hasTags: multiselect && displayValues.length > 0,
            compact,
            ref: this.handleValueContainerRef,
        };

        return (
            <>
                {this.props.autocomplete ? (
                    // @ts-expect-error $TSFixMeError: Type '{ value: InputValue | undefined; ref: (c: HTMLElement | null) => HTMLElement | null; onClick: (() => void) | undef...
                    <Input {...autocompleteProps} />
                ) : (
                    <ComboInputElement {...comboInputProps}>
                        {leftVariant}
                        <ValueContainerElement {...valueContainerProps}>
                            {multiselect &&
                                displayValues.map(([key, val], index) => (
                                    <Chip
                                        key={key}
                                        ref={this.handleTagRef(key)}
                                        value={index}
                                        disabled={disabled}
                                        onRemove={this.handleRemoveItem}
                                        data-test-chip={key}
                                    >
                                        {val}
                                    </Chip>
                                ))}
                            {!multiselect && !searchValue && (
                                <SingleValueElement size={size} disabled={disabled}>
                                    {displayedInputValue}
                                </SingleValueElement>
                            )}
                            {multiselect && compact ? null : <AutosizeInputComponent {...inputProps} />}
                        </ValueContainerElement>

                        {/*
                        // NOTE: Let's ignore clearing for now
                        (multiselect && searchValue) || (!multiselect && focused && selected)
                            ? <ClearIcon onClick={this.props.onClear()} />
                            : <ExpandIcon />
                        */}

                        {multiselect && compact && restTagCount > 0 ? (
                            <RestTagCounterContainer>+{this.state.restTagCount}</RestTagCounterContainer>
                        ) : null}

                        {renderDropdownIcon && (
                            <RightDetailContainer>
                                {rightVariant}
                                {renderDropdownIcon && renderDropdownIcon(renderDropdownIconProps)}
                            </RightDetailContainer>
                        )}
                    </ComboInputElement>
                )}

                <OpenableOverlay
                    sizeToAnchor={sizeToAnchor}
                    scrollOnOverflow
                    opened={opened && this.shouldShowDropdown()}
                    anchorElement={this.getAnchorElement}
                    innerRef={this.handleOverlayRef}
                    ref={this.handleOverlayComponentRef}
                    maxHeight={overlayMaxHeight}
                    portalContainerId={portalContainerId}
                    onMaxSizeChange={this.handleMaxSizeChange}
                    onMount={this.handleOverlayOnMount}
                >
                    <>
                        {multiselect === true && compact === true && (
                            <>
                                <SearchContainerElement>
                                    {/* @ts-expect-error $TSFixMeError: Property 'name' is missing in type '{ indicativeIcon: IconName.Search; placeholder: ReactNode; disabled: boolean | undef... */}
                                    <Input
                                        {...inputProps}
                                        indicativeIcon={IconName.Search}
                                        placeholder={searchPlaceholder}
                                    />
                                </SearchContainerElement>
                                <Divider />
                            </>
                        )}
                        <SelectList {...listProps}>{this.items}</SelectList>
                    </>
                </OpenableOverlay>
                {!multiselect && shadowField && <input type="hidden" name={name} value={selected?.toString() ?? ''} />}
            </>
        );
    }
}

// There is an ongoing issue with defaultProps vs HOC in typescript, follow https://github.com/Microsoft/TypeScript/issues/23812 for details
const SelectElement = withScrollAway(withClickAway(DefaultSelect));

export default SelectElement as React.ComponentType<SelectDefaultedProps>;
